import { useMemo } from "react";

export interface ChannelGroup {
    // Unique per group — safe to use as a React key (channel ids repeat when
    // a project holds the same channel more than once).
    key: string;
    channel: {
        id: number;
        name: string;
        brand_project_channel_id?: number | null;
        // Internal-only; present only for internal usertypes.
        internal_status?: string | null;
        project_option_selections?: any[];
        // Staff-written description of this instance (null when unset).
        instance_label?: string | null;
    };
    videos: any[];
}

/**
 * Groups videos by channel INSTANCE (brand_projects_channels row), matching
 * them to the given project channels. A project may hold the same generic
 * channel multiple times (e.g. 2× PDP) — each instance gets its own group,
 * labelled "PDP", "PDP #2", ... Videos are matched by their
 * brand_project_channel_id; videos without one (legacy) fall back to the
 * first instance of their content_channel_id. Used by both the private
 * Downloads tab and the public order page to render channel-grouped
 * accordions.
 */
const UNASSIGNED_CHANNEL_ID = -1;

// "PDP #2" + "Holiday unboxing" -> "PDP #2 · Holiday unboxing". The label sits
// beside the generated name, never replaces it (mirrors the API's
// withChannelLabel).
export const withInstanceLabel = (name: string, label?: string | null): string =>
    label ? `${name} · ${label}` : name;
export const UNASSIGNED_CHANNEL_NAME = "Other";

export interface LabelledChannelInstance {
    key: string;
    channel: any;
    instanceId: number | null;
    name: string;
}

/**
 * One entry per channel instance, labelled "PDP", "PDP #2", ... in project
 * order, with the instance's optional label beside it ("PDP #2 · Holiday").
 * Shared with the zip download so its folders match the Downloads tab.
 */
export function labelChannelInstances(channels: any[]): LabelledChannelInstance[] {
    const seen = new Set<string>();
    const nameCounts: Record<string, number> = {};
    const result: LabelledChannelInstance[] = [];
    (channels || []).forEach((ch: any) => {
        const instanceId = ch.brand_project_channel_id ?? null;
        const key = instanceId != null ? `inst-${instanceId}` : `ch-${ch.id}`;
        if (seen.has(key)) return;
        seen.add(key);
        const baseName = ch.name || "Channel";
        nameCounts[baseName] = (nameCounts[baseName] || 0) + 1;
        const name = withInstanceLabel(
            nameCounts[baseName] > 1
                ? `${baseName} #${nameCounts[baseName]}`
                : baseName,
            ch.instance_label
        );
        result.push({ key, channel: ch, instanceId, name });
    });
    return result;
}

export function useChannelGroups(
    channels: any[],
    videos: any[]
): { channelGroups: ChannelGroup[]; hasChannels: boolean } {
    const channelGroups = useMemo(() => {
        const grouped: Record<string, ChannelGroup> = {};
        const groupOrder: string[] = [];
        // instance id -> group key; channel id -> first group key (legacy
        // fallback for videos that only carry content_channel_id).
        const keyByInstance: Record<string, string> = {};
        const firstKeyByChannel: Record<string, string> = {};

        labelChannelInstances(channels).forEach(({ key, channel: ch, instanceId, name }) => {
            grouped[key] = {
                key,
                channel: {
                    id: ch.id,
                    name,
                    brand_project_channel_id: instanceId,
                    internal_status: ch.internal_status ?? null,
                    project_option_selections: ch.project_option_selections || [],
                    instance_label: ch.instance_label ?? null,
                },
                videos: [],
            };
            groupOrder.push(key);
            if (instanceId != null) keyByInstance[`${instanceId}`] = key;
            if (!firstKeyByChannel[`${ch.id}`]) {
                firstKeyByChannel[`${ch.id}`] = key;
            }
        });

        const unassigned: any[] = [];
        (videos || []).forEach((video: any) => {
            const instanceId = video.brand_project_channel_id;
            const chId = video.content_channel_id;
            const key =
                (instanceId != null && keyByInstance[`${instanceId}`]) ||
                (chId != null && firstKeyByChannel[`${chId}`]) ||
                null;
            if (key && grouped[key]) {
                grouped[key].videos.push(video);
            } else {
                unassigned.push(video);
            }
        });

        const result = groupOrder.map((k) => grouped[k]);
        if (unassigned.length > 0 && (channels || []).length > 0) {
            result.push({
                key: "unassigned",
                channel: {
                    id: UNASSIGNED_CHANNEL_ID,
                    name: UNASSIGNED_CHANNEL_NAME,
                    brand_project_channel_id: null,
                    internal_status: null,
                    project_option_selections: [],
                },
                videos: unassigned,
            });
        }
        return result;
    }, [videos, channels]);

    const hasChannels = (channels || []).length > 0;

    return { channelGroups, hasChannels };
}
